<script>
    import tinyPumperIncidentsService from '@/services/tinyPumperIncidents.service';

    /**
     * What the last TinyPumper health run was reasoning with, on /tiny-pumper-health.
     */
    export default {
        name: 'TinyPumperHealthPolicyPanel',
        data() {
            return {
                // null, not {} — "we have not asked yet" must not render as a policy of zeros.
                policy: null,
                isLoading: false,
                hasReadFailed: false,
                isExpanded: false
            };
        },
        computed: {
            hasPolicy() {
                return !!this.policy?.hasPolicy;
            },
            publishedAtLabel() {
                if (!this.policy?.publishedAt) return 'at an unknown time';

                return new Date(this.policy.publishedAt).toLocaleString();
            },
            getThresholds() {
                if (!this.hasPolicy) return [];

                return [
                    {
                        label: 'Silent after',
                        value: `${this.policy.silenceThresholdHours} h`,
                        note: 'A device quiet longer than this has a problem. Also the sync-lag ceiling.'
                    },
                    {
                        label: 'Vendor outage ratio',
                        value: this.policy.vendorOutageSilentRatio,
                        note: 'Fraction of the fleet silent before LevelCon is the simplest explanation'
                    },
                    {
                        label: 'Gateway correlation window',
                        value: `${this.policy.gatewayCorrelationWindowHours} h`,
                        note: 'How close together devices behind one gateway must go quiet to share a cause'
                    },
                    {
                        label: 'Low battery',
                        value: `${this.policy.lowBatteryPercent} %`,
                        note: 'At or below this the battery, not the device, is the explanation'
                    },
                    {
                        label: 'Battery auto-snooze',
                        value: `${this.policy.batteryAutoSnoozeDays} d`,
                        note: 'Silence bought so a truck roll has time to happen'
                    },
                    {
                        label: 'Battery slope window',
                        value: `${this.policy.batterySlopeWindowHours} h`,
                        note: 'How far back battery history is read to fit a drain rate'
                    },
                    {
                        label: 'Battery drain',
                        value: `${this.policy.batteryDrainPercentPerDay} %/day`,
                        note: 'At or above this the battery is the explanation'
                    },
                    {
                        label: 'Minimum slope samples',
                        value: this.policy.minimumBatterySlopeSamples,
                        note: 'Below this many readings no drain rate is claimed'
                    },
                    {
                        label: 'Gateway silent after',
                        value: `${this.policy.gatewayHeardThresholdHours} h`,
                        note: 'A gateway that has not checked in for longer than this is down'
                    },
                    {
                        label: 'Minimum sensors to blame a scope',
                        value: this.policy.minimumSensorsForScopeCollapse,
                        note: 'Below this a gateway or a site cannot be blamed on the evidence'
                    },
                    {
                        label: 'Max messages per run',
                        value: this.policy.maxMessagesPerRun,
                        note: 'Above this the run posts one grouped line instead'
                    },
                    {
                        label: '@channel mentions',
                        value: this.policy.channelWideMentionsEnabled ? 'Allowed' : 'Off',
                        note: 'Whether a vendor outage or ingestion failure may page everyone'
                    }
                ];
            }
        },
        methods: {
            loadPolicy() {
                this.isLoading = true;
                this.hasReadFailed = false;

                return tinyPumperIncidentsService
                    .policy()
                    .then(policy => {
                        this.policy = policy ?? null;
                    })
                    .catch(() => {
                        this.policy = null;
                        this.hasReadFailed = true;
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            },
            toggleExpanded() {
                this.isExpanded = !this.isExpanded;
            }
        },
        mounted() {
            this.loadPolicy();
        }
    };
</script>

<template>
    <div class="tp-policy-panel card">
        <button type="button" class="tp-policy-panel__header btn btn-link text-decoration-none text-start" @click="toggleExpanded">
            <i class="bi me-2" :class="isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
            <span class="fw-semibold">Engine policy — what the last run used</span>
            <small v-if="hasPolicy" class="text-muted ms-2">published {{ publishedAtLabel }}</small>
            <small v-else-if="isLoading" class="text-muted ms-2">loading…</small>
        </button>

        <div v-show="isExpanded" class="tp-policy-panel__body card-body pt-0">
            <p class="text-muted small mb-3">
                These are Azure App Settings on the TinyPumper function, not settings you can edit here. The engine writes
                down what it reasoned with on every run; this is a copy of that, not a live control.
            </p>

            <div v-if="hasReadFailed" class="alert alert-warning mb-0" role="alert">
                <i class="bi bi-question-circle-fill me-2"></i>
                We could not read the published policy. The thresholds below are unknown — do not assume the defaults.
            </div>

            <div v-else-if="!hasPolicy && !isLoading" class="alert alert-secondary mb-0" role="alert">
                <i class="bi bi-hourglass-split me-2"></i>
                The engine has not published a policy yet. It writes one at the start of every health run, so this fills in
                after the next one.
            </div>

            <div v-else-if="hasPolicy" class="table-responsive">
                <table class="table table-sm tp-policy-panel__table mb-0">
                    <tbody>
                        <tr v-for="threshold in getThresholds" :key="`tp-policy-${threshold.label}`">
                            <th scope="row" class="fw-normal">{{ threshold.label }}</th>
                            <td class="font-monospace text-nowrap">{{ threshold.value }}</td>
                            <td class="text-muted small">{{ threshold.note }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .tp-policy-panel {
        &__header {
            padding: 0.5rem 0.75rem;
            color: inherit;
        }

        &__table {
            th {
                width: 16rem;
            }
        }
    }
</style>
